import {Accordion, AccordionDetails, AccordionSummary, Stack, Typography, IconButton, Box} from "@mui/material";
import AccordionIcon from "pages/Faq/components/AccordionIcon";
import Text from "components/Text";
import {Question} from "pages/Faq/types";
import Tag from "components/Tag";
import React, {useCallback, useState} from "react";
import DocumentCard from "pages/Faq/components/DocumentCard";
import Copy from "assets/icons/Copy";

interface QuestionItemProps {
	question: Question;
	isComeFromLink: boolean;
	id: string;
	openByLink: string | null;
	selectedCategoryId: string | null | undefined;
	selectedSubCategoryId: string | null | undefined;
}

const QuestionItem = ({question, openByLink, isComeFromLink, id, selectedSubCategoryId, selectedCategoryId	}: QuestionItemProps) => {
	const isArchived = question.isArchived;
	const [expanded, setExpanded] = useState(openByLink === question.id && isComeFromLink);

	const handleCopy = useCallback((e: React.MouseEvent) => {
		e.stopPropagation();
		let url = `${window.location.origin}${window.location.pathname}?id=${question.id}`;
		if (selectedCategoryId) {
			url += `&category=${selectedCategoryId}`;
			if (selectedSubCategoryId) {
				url += `&subcategory=${selectedSubCategoryId}`;
			}
		}

		navigator.clipboard.writeText(url);
	}, [question.id, selectedCategoryId, selectedSubCategoryId]);

	return (
		<Accordion
			disableGutters
			expanded={expanded}
			onChange={(event, isExpanded) => setExpanded(isExpanded)}
			id={id}
			sx={{
				mb: 2,
				p: 0,
				boxShadow: 'none',
				'&:before': {display: 'none'},
				borderRadius: '24px !important',
				overflow: 'hidden',
				backgroundColor: isArchived ? '#EDEFF3' : '#F9F9FB',
			}}
		>
			<AccordionSummary
				expandIcon={<AccordionIcon/>}
				sx={{
					'& .MuiAccordionSummary-content': {m: 0},
					p: 3,
					alignItems: 'flex-start',

					'& .MuiAccordionSummary-expandIconWrapper': {
						width: '32px',
						height: '32px',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						color: isArchived ? '#BDBDBD' : '#1A1A1A',
						marginTop: isArchived ? '38px' : '0px',

						'& svg': {
							width: '32px',
							height: '32px',
						}
					}
				}}
			>
				<Stack spacing={1.5} sx={{width: '100%'}}>
					{isArchived && (
						<Box
							sx={{
								backgroundColor: '#2B58D1',
								color: '#fff',
								borderRadius: '4px',
								px: 1.5,
								py: 0.5,
								width: 'fit-content',
								fontSize: '12px',
								lineHeight: '18px',
								fontWeight: 500,
							}}
						>
							Архивный вопрос
						</Box>
					)}

					<Stack direction="row" alignItems="flex-start" spacing={1} justifyContent="space-between">
						<Typography
							variant={'h4'}
							sx={{
								fontWeight: 700,
								pr: 2,
								color: isArchived ? '#A0A0A0' : '#1A1A1A',
								lineHeight: '32px',
								display: 'flex',
								alignItems: 'center'
							}}
						>
							{question.questionText}
							{expanded && (
								<IconButton onClick={handleCopy} size="small" sx={{ml: 1, color: 'error.main'}}>
									<Copy fontSize="small"/>
								</IconButton>
							)}
						</Typography>
					</Stack>

					{question.tags && question.tags.length > 0 && (
						<Stack direction={'row'} spacing={1}>
							{question.tags.map((t) => (
								<Tag
									key={`question-${question.id}-tag-${t.tag.id}`}
									title={t.tag.title}
								/>
							))}
						</Stack>
					)}
				</Stack>
			</AccordionSummary>

			<AccordionDetails sx={{px: 3, pb: 4, pt: 0}}>
				<Stack spacing={4}>
					<Text variant={'p1'} sx={{color: 'grey.200'}} text={question.answerText}/>

					{question.documents && question.documents.length > 0 && (
						<Stack spacing={2}>
							<Typography variant="h5" sx={{fontWeight: 700}}>
								Документы по теме
							</Typography>
							<Stack spacing={2}>
								{question.documents.map((doc) => (
									<DocumentCard key={doc.id} doc={{
										title: doc.title,
										type: doc.type? { title: doc.type.title, color: doc.type.color } : null,
										audiences: doc.audiences ?? [],
										publishedAt: doc.publishedAt,
										file: { name: doc.file.name, url: doc.file.url }
									}}/>
								))}
							</Stack>
						</Stack>
					)}
				</Stack>
			</AccordionDetails>
		</Accordion>
	);
};

export default React.memo(QuestionItem);